/**
 * S13, Files. A file browser that makes file storage feel like a folder, and
 * that defuses one specific footgun before it fires.
 *
 * THE FOOTGUN IS THE REASON THIS SCREEN EXISTS. A person's coding agent
 * committed a logo to design/assets, the platform synced it, and the file now
 * sits in this list looking exactly like the photograph they uploaded from
 * their phone. Deleting it appears to work and then it comes back on the next
 * deploy, and there is no way to work out why from anywhere else in the
 * product. So the row carries a flat "From your code" chip, and its delete
 * confirm says the true thing in three sentences instead of asking "are you
 * sure": it comes back, here is where it lives in your code, delete it there
 * to remove it for good.
 *
 * WHAT THE SCREEN IS BUILT FROM, in order of the zones:
 *   header    the H1 and its grounding line
 *   bar       the folder breadcrumb, the size cap stated in advance, Upload
 *   tree      the folders, which are prefixes on a name, not objects
 *   tray      transfers in flight, and the ones that did not land
 *   list      the objects, as ledger rows
 *   footer    the count and the file storage bar
 *   advanced  the exact totals, the store's own name, the sharing rules
 *
 * FOLDERS ARE PREFIXES, and the screen refuses to pretend otherwise. There is
 * no "New folder" control, because a folder with nothing in it cannot exist in
 * file storage: the folder appears when the first file lands in it and is gone
 * when the last one leaves. A control that appeared to make one would be the
 * kind of small lie that costs a person an afternoon later.
 *
 * THE CAPS READ AS NORMAL CONDITIONS, never as errors, per brief F8. The per
 * file size cap is on the screen before anyone drags anything, and the list
 * cap says "Showing the first 100 files." the same way the table grid says it
 * about rows. Neither one is a failure and neither one gets a warning colour.
 *
 * ONE THING MOVES: the upload tray. Nothing else on this screen animates while
 * a transfer is running, and each row's track is a width the clock sets rather
 * than an animation of its own.
 *
 * STATES, all deep linkable, because a state a reviewer cannot open is a state
 * nobody reviews:
 *   ?state=uploading         three transfers at staggered speeds, all land
 *   ?state=upload-stopped    one stops partway, the other two land
 *   ?state=upload-refused    one over the size cap, one kind we do not hold
 *   ?state=over-quota        file storage over the limit, uploads paused
 *   ?state=from-code         the design/assets folder, every row badged
 *   ?state=delete-from-code  the footgun confirm, open, on logo.svg
 *   ?state=preview           the file sheet, open, on the first image
 *   ?state=empty-folder      the selected folder with nothing left in it
 *   ?state=empty-store       an app that has not stored anything yet
 *   ?state=loading           the skeletons, held open
 *
 * // reads through environmentOf(); do not flatten.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { CopyField } from '../../components/patterns/CopyField';
import { DestructiveConfirm } from '../../components/patterns/DestructiveConfirm';
import { EmptyState } from '../../components/patterns/EmptyState';
import { FileGlyph, FileList, FileRow, fileHash, fileKind, kindWord } from '../../components/patterns/FileRow';
import { Notice } from '../../components/patterns/Notice';
import { Skeleton } from '../../components/patterns/Skeleton';
import { UploadLinkPanel } from '../../components/patterns/UploadLinkPanel';
import { UsageBar } from '../../components/patterns/UsageBar';
import { whenLabelOrNull } from '../../components/patterns/DeployRow';
import {
  DropZone,
  FilePickerButton,
  MAX_UPLOAD_BYTES,
  durationFor,
  refusalFor,
  refusalSentence,
  uploadProgress,
  useUploadClock,
} from '../../components/patterns/UploadDrop';
import type { PickedFile } from '../../components/patterns/UploadDrop';
import { Button } from '../../components/primitives/Button';
import { Checkbox } from '../../components/primitives/Checkbox';
import { Dialog } from '../../components/primitives/Dialog';
import { Select } from '../../components/primitives/Select';
import { showToast } from '../../components/primitives/Toast';

import { environmentOf, getProject, getUsage, listFiles } from '../../lib/api';
import { useProjects } from '../../lib/auth/use-app-data';
import { bytes as formatBytes, count } from '../../lib/format';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { t } from '../../lib/i18n/t';
import { UPLOAD_DEMO, UPLOAD_DEMO_REFUSED, UPLOAD_DEMO_STOPPED } from '../../lib/mock/seed';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { FileObj, FileUpload, UploadSeedItem, UsageMetric } from '../../lib/types';

import '../../styles/screens/files.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/** The list cap, stated as a normal condition the way the row cap is. */
export const FILES_SHOWN = 100;

/** The folder the repo syncs into. The footgun lives here and nowhere else. */
export const CODE_FOLDER = 'design/assets';

const STATES = [
  'files',
  'uploading',
  'upload-stopped',
  'upload-refused',
  'over-quota',
  'from-code',
  'delete-from-code',
  'preview',
  'empty-folder',
  'empty-store',
  'loading',
] as const;

type StateName = (typeof STATES)[number];

/* ------------------------------------------------------------- the folders */

interface FolderNode {
  /** The prefix itself: "design/assets". The root is the empty string. */
  path: string;
  name: string;
  count: number;
  bytes: number;
  children: FolderNode[];
}

function folderOf(path: string): string {
  const cut = path.lastIndexOf('/');
  return cut === -1 ? '' : path.slice(0, cut);
}

/** An unknown updatedAt (cloud-api has none to give) sorts as oldest, not NaN. */
function timeOrZero(iso: string): number {
  const at = new Date(iso).getTime();
  return Number.isNaN(at) ? 0 : at;
}

/** Everything at or under a prefix, because clicking design should show what
 *  is in design/assets rather than an empty room with a folder in it. */
function inFolder(path: string, folder: string): boolean {
  if (folder === '') return true;
  return path.startsWith(`${folder}/`);
}

function relativeName(path: string, folder: string): string {
  return folder === '' ? path : path.slice(folder.length + 1);
}

function buildTree(files: FileObj[]): FolderNode {
  const root: FolderNode = { path: '', name: '', count: 0, bytes: 0, children: [] };

  for (const file of files) {
    root.count += 1;
    root.bytes += file.bytes;

    const segments = folderOf(file.path).split('/').filter(Boolean);
    let node = root;
    let prefix = '';

    for (const segment of segments) {
      prefix = prefix === '' ? segment : `${prefix}/${segment}`;
      let child = node.children.find((one) => one.path === prefix);
      if (!child) {
        child = { path: prefix, name: segment, count: 0, bytes: 0, children: [] };
        node.children.push(child);
      }
      child.count += 1;
      child.bytes += file.bytes;
      node = child;
    }
  }

  const sortDeep = (node: FolderNode) => {
    node.children.sort((a, b) => a.name.localeCompare(b.name));
    node.children.forEach(sortDeep);
  };
  sortDeep(root);

  return root;
}

function countLabel(n: number): string {
  if (n === 0) return t('No files');
  if (n === 1) return t('1 file');
  return t('{n} files', { n: count(n) });
}

/**
 * The fixture leaves publicUrl null and this does not invent it: a public link
 * is a pure function of the environment's web address and the object's own
 * path, which is exactly the relationship the real service has.
 */
function publicLink(file: FileObj, webAddress: string): string {
  return file.publicUrl ?? `${webAddress}/${file.path}`;
}

function landedFile(upload: FileUpload, projectId: string): FileObj {
  const path = upload.folder === '' ? upload.name : `${upload.folder}/${upload.name}`;
  return {
    id: `upl_${upload.id}`,
    projectId,
    path,
    name: upload.name,
    bytes: upload.bytes,
    contentType: upload.contentType,
    source: 'uploaded',
    updatedAt: new Date(upload.startedAt).toISOString(),
    publicUrl: null,
  };
}

function seedToUpload(item: UploadSeedItem, folder: string, index: number, key: string): FileUpload {
  return {
    id: `upl_${key}_${index}`,
    name: item.name,
    folder,
    bytes: item.bytes,
    contentType: item.contentType,
    startedAt: Date.now(),
    durationMs: item.durationMs,
    refusal: item.refusal,
  };
}

function pickedToUpload(file: PickedFile, folder: string, index: number): FileUpload {
  const refusal = refusalFor(file);
  return {
    id: `upl_${Date.now()}_${index}`,
    name: file.name,
    folder,
    bytes: file.bytes,
    contentType: file.contentType,
    startedAt: Date.now(),
    durationMs: refusal === null ? durationFor(file.bytes) : 0,
    refusal,
  };
}

/* ---------------------------------------------------------------- the tree */

function FolderBranch({
  node,
  depth,
  selected,
  onPick,
}: {
  node: FolderNode;
  depth: number;
  selected: string;
  onPick: (path: string) => void;
}) {
  return (
    <li className="ftree__item">
      <button
        type="button"
        className="ftree__row"
        data-on={selected === node.path ? 'yes' : 'no'}
        style={{ paddingInlineStart: `calc(var(--space-3) + ${depth} * var(--space-4))` }}
        onClick={() => onPick(node.path)}
        aria-current={selected === node.path ? 'true' : undefined}
      >
        <span className="ftree__glyph" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.3" focusable="false">
            <path d="M1.8 4.2h4.1l1.3 1.6h7v6.4a.8.8 0 0 1-.8.8H2.6a.8.8 0 0 1-.8-.8Z" strokeLinejoin="round" />
          </svg>
        </span>
        <span className="ftree__name mono">{node.path === '' ? t('All files') : node.name}</span>
        <span className="ftree__count mono">{count(node.count)}</span>
      </button>

      {node.children.length > 0 ? (
        <ul className="ftree__list">
          {node.children.map((child) => (
            <FolderBranch
              key={child.path}
              node={child}
              depth={depth + 1}
              selected={selected}
              onPick={onPick}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/* --------------------------------------------------------------- the sheet */

/**
 * The file sheet. Everything a person can do to one file lives here rather
 * than behind a kebab on the row: one target per row is what makes this screen
 * work on a phone, and it is the only place a copy field can be full width
 * instead of squeezed into a table cell.
 */
function FileSheet({
  file,
  webAddress,
  onClose,
  onDelete,
}: {
  file: FileObj;
  webAddress: string;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const [expiry, setExpiry] = useState('1d');
  const [openToAnyone, setOpenToAnyone] = useState(true);
  const body = useRef<HTMLDivElement | null>(null);

  /**
   * Open at the top of the sheet, on a short screen as well as a tall one.
   * The dialog moves focus into the panel when it opens, and a browser
   * scrolls a focused element into view its own way: on a laptop with the
   * inspector open, that meant this sheet opened with the picture already
   * scrolled off the top of it. The scroll position is set after the focus
   * rather than instead of it, so the reading order still starts at the file.
   */
  useEffect(() => {
    const panel = body.current?.closest('.dialog__panel');
    if (panel instanceof HTMLElement) panel.scrollTop = 0;
  }, []);

  const kind = fileKind(file.contentType, file.name);
  const link = publicLink(file, webAddress);
  const signed = `${link}?key=${fileHash(file.path).toString(36)}&for=${expiry}`;

  return (
    <Dialog
      open
      onClose={onClose}
      title={file.name}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('Close')}
          </Button>
          {onDelete ? (
            <Button variant="danger-outline" onClick={onDelete}>
              {t('Delete')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="fsheet" ref={body}>
        {/* Focus lands on the preview rather than on Close, because the panel
            scrolls and focusing the footer would open this sheet with the
            picture already scrolled off the top of it. */}
        <div className="fsheet__preview" data-autofocus="" tabIndex={-1} aria-label={file.name}>
          <FileGlyph kind={kind} name={file.path} size="lg" />
        </div>

        {file.source === 'from-your-code' ? (
          <Notice tone="quiet" title={t('From your code')}>
            <p>
              {t('This file comes from your repository. It comes back the next time you deploy.')}
            </p>
          </Notice>
        ) : null}

        <dl className="fsheet__facts">
          <div className="fsheet__row">
            <dt className="fsheet__key">{t('Size')}</dt>
            <dd className="fsheet__value mono">{formatBytes(file.bytes)}</dd>
          </div>
          <div className="fsheet__row">
            <dt className="fsheet__key">{t('Kind')}</dt>
            <dd className="fsheet__value">{kindWord(kind)}</dd>
          </div>
          {whenLabelOrNull(file.updatedAt) ? (
            <div className="fsheet__row">
              <dt className="fsheet__key">{t('Changed')}</dt>
              <dd className="fsheet__value mono">{whenLabelOrNull(file.updatedAt)}</dd>
            </div>
          ) : null}
        </dl>

        <div className="fsheet__link">
          <p className="fsheet__label">{t('Public link')}</p>
          <CopyField label={t('Public link')} value={link} truncateAt={56} />
        </div>

        {/* Advanced shows the mechanism. Simple shows the outcome, which is
            the link, and has no vocabulary for the rest of this. */}
        <AdvancedDisclosure offerAdvanced>
          <div className="fsheet__adv">
            <div className="fsheet__link">
              <p className="fsheet__label">{t('Where it sits')}</p>
              <CopyField label={t('File path')} value={file.path} size="inline" />
            </div>

            <dl className="fsheet__facts">
              <div className="fsheet__row">
                <dt className="fsheet__key">{t('Exact size')}</dt>
                <dd className="fsheet__value mono">
                  {file.bytes === 1 ? t('1 byte') : t('{n} bytes', { n: count(file.bytes) })}
                </dd>
              </div>
              <div className="fsheet__row">
                <dt className="fsheet__key">{t('Content type')}</dt>
                <dd className="fsheet__value mono">{file.contentType}</dd>
              </div>
            </dl>

            {USE_MOCK ? (
              <>
                <Checkbox
                  label={t('Anyone with the link can open this file')}
                  help={t('Turn this off and only your app can read it.')}
                  checked={openToAnyone}
                  onChange={(event) => setOpenToAnyone(event.target.checked)}
                />

                <div className="fsheet__link">
                  <p className="fsheet__label">{t('Link that expires')}</p>
                  <Select
                    label={t('Stops working after')}
                    value={expiry}
                    onChange={(event) => setExpiry(event.target.value)}
                    options={[
                      { value: '1h', label: t('1 hour') },
                      { value: '1d', label: t('1 day') },
                      { value: '7d', label: t('7 days') },
                    ]}
                  />
                  <CopyField label={t('Link that expires')} value={signed} truncateAt={56} />
                  <p className="fsheet__note">
                    {t('Anyone with this link can open the file until it stops working.')}
                  </p>
                </div>
              </>
            ) : null}
          </div>
        </AdvancedDisclosure>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------- the footgun confirm */

/**
 * The delete confirm for a file that came from the repository, and it is the
 * dialog this whole screen was worth building for. Screen spec S13 and 6.4,
 * verbatim.
 *
 * It is a Dialog rather than the destructive confirm, on purpose: the
 * destructive confirm enumerates what survives and what is lost, and here
 * nothing is lost. The file comes back. The thing a person needs is three
 * sentences of explanation and the one place to go and do it properly, which
 * an enumeration of glyph rows cannot say.
 *
 * No type to confirm, per the governing rule in 4.5: typing is reserved for
 * the irreversible, and this is the most reversible delete in the product.
 */
function RepoFileConfirm({
  file,
  onClose,
  onConfirm,
}: {
  file: FileObj;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('Delete {name}?', { name: file.name })}
      footer={
        <>
          <Button variant="outline" onClick={onClose} data-autofocus="">
            {t('Cancel')}
          </Button>
          <Button variant="danger-outline" onClick={onConfirm}>
            {t('Delete anyway')}
          </Button>
        </>
      }
    >
      <div className="frepo">
        <p className="frepo__say">
          {t(
            'This file comes from your repository at {path}. Deleting it here removes it until your next deploy, and then it comes back.',
            { path: file.path },
          )}
        </p>
        <p className="frepo__say">{t('To remove it for good, delete it from your code.')}</p>
        <CopyField label={t('File in your code')} value={file.path} size="inline" />
      </div>
    </Dialog>
  );
}

/* -------------------------------------------------------------- the screen */

export default function Files() {
  const slug = useProjectSlug() ?? '';
  const navigate = useNavigate();
  const { state } = useScreenState<StateName>(STATES, 'files');

  const scenarioId = useDemoStore((s) => s.scenarioId);
  const speed = useDemoStore((s) => s.prefs.speed);

  const cachedProjects = useProjects();
  const cachedProject = slug ? cachedProjects.find((p) => p.slug === slug) : undefined;
  const { data: fetchedProject, isLoading: isProjectLoading, error: projectError } = useAsync(
    () => (slug ? getProject(slug) : Promise.resolve(null)),
    [slug, scenarioId],
  );
  const project = fetchedProject ?? cachedProject;
  const projectId = project?.id ?? '';

  const { data: filesData, error: filesError } = useAsync(
    () => (projectId ? listFiles(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  const { data: usage } = useAsync(
    () => (projectId ? getUsage(projectId) : Promise.resolve(null)),
    [projectId, scenarioId],
  );

  const stored = useMemo(() => filesData ?? [], [filesData]);

  /* ---- what the person is looking at ---- */

  const [folder, setFolder] = useState('');
  const [removed, setRemoved] = useState<string[]>([]);
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [shown, setShown] = useState(FILES_SHOWN);
  const [opened, setOpened] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // The dock can swap the whole world underneath a screen. A folder selection
  // and a session's deletions belong to the account that was here.
  useEffect(() => {
    setFolder('');
    setRemoved([]);
    setUploads([]);
    setOpened(null);
    setDeleting(null);
  }, [slug, scenarioId]);

  /* ---- the tray, on the clock ---- */

  const anyMoving = uploads.some(
    (one) => uploadProgress(one, Date.now(), speed).status === 'uploading',
  );
  const now = useUploadClock(anyMoving);

  const tray = uploads.map((one) => ({ upload: one, at: uploadProgress(one, now, speed) }));
  const inFlight = tray.filter((one) => one.at.status === 'uploading');
  const notLanded = tray.filter((one) => one.at.status === 'didnt-upload');
  const landed = tray.filter((one) => one.at.status === 'landed');

  function addFiles(picked: PickedFile[]) {
    captureEvent('file_upload_started', {
      project_slug: project?.slug,
      file_count: picked.length,
      folder,
    });
    setUploads((was) => [
      ...was,
      ...picked.map((file, index) => pickedToUpload(file, folder, was.length + index)),
    ]);
  }

  /* ---- the fakeable states ---- */

  const armed = useRef<string | null>(null);

  useEffect(() => {
    if (!project) return;
    const key = `${slug}:${state}`;
    if (armed.current === key) return;
    armed.current = key;

    const seeded: Record<string, UploadSeedItem[]> = {
      uploading: UPLOAD_DEMO,
      'upload-stopped': UPLOAD_DEMO_STOPPED,
      'upload-refused': UPLOAD_DEMO_REFUSED,
    };

    const list = seeded[state];
    if (list) {
      // Restarted rather than resumed: timelines and trays alike would
      // otherwise open on transfers that finished before the page was on
      // screen, which is the one thing a demo cannot recover from.
      setUploads(list.map((item, index) => seedToUpload(item, 'images', index, state)));
      setFolder('images');
      return;
    }

    if (state === 'from-code' || state === 'delete-from-code') {
      setFolder(CODE_FOLDER);
      return;
    }

    if (state === 'files') setUploads([]);
  }, [state, slug, project]);

  /* ---- the rows ---- */

  /**
   * A transfer that landed becomes a row, derived rather than written: the
   * clock decides, every render, which uploads are files now. Nothing has to
   * fire on completion, which means nothing can miss it.
   *
   * The memo keys off the landed ids rather than the objects, because the
   * objects are rebuilt on every tick and would defeat the memo entirely.
   */
  const landedKey = landed.map((one) => one.upload.id).join(',');

  const held = useMemo(() => {
    const settled = landed.map((one) => landedFile(one.upload, projectId));
    return [...settled, ...stored].filter((file) => !removed.includes(file.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- landedKey stands in for landed
  }, [stored, removed, projectId, landedKey]);

  const visible = useMemo(
    () =>
      held
        .filter((file) => inFolder(file.path, folder))
        .sort((a, b) => timeOrZero(b.updatedAt) - timeOrZero(a.updatedAt)),
    [held, folder],
  );

  const tree = useMemo(() => buildTree(held), [held]);

  const openFile = visible.find((file) => file.id === opened) ?? null;
  const deletingFile = visible.find((file) => file.id === deleting) ?? null;

  // The forced preview and the forced footgun confirm, once the rows exist.
  const posed = useRef<string | null>(null);
  useEffect(() => {
    if (visible.length === 0) return;
    const key = `${slug}:${state}`;
    if (posed.current === key) return;

    if (state === 'preview') {
      const image = visible.find((file) => fileKind(file.contentType, file.name) === 'image');
      if (!image) return;
      posed.current = key;
      setOpened(image.id);
      return;
    }

    if (state === 'delete-from-code') {
      const fromCode = visible.find((file) => file.source === 'from-your-code');
      if (!fromCode) return;
      posed.current = key;
      setDeleting(fromCode.id);
    }
  }, [state, slug, visible]);

  /* ---- usage, and the limit ---- */

  const storage: UsageMetric | null = useMemo(() => {
    const metric = usage?.metrics.find((one) => one.resource === 'file-storage');
    if (!metric) return null;
    if (state !== 'over-quota') return metric;
    // Forced, the way the overview forces its band: a reviewer asking to see
    // the over limit case wants the over limit case.
    const used = Math.round(metric.limit * 1.06);
    return { ...metric, used, state: 'over' };
  }, [usage, state]);

  const overQuota = storage !== null && storage.used >= storage.limit;

  useEffect(() => {
    if (project) {
      captureEvent('files_screen_viewed', {
        project_slug: project.slug,
        file_count: visible.length,
        folder,
        over_quota: overQuota,
      });
    }
  }, [project, visible.length, folder, overQuota]);

  /* ---- the shape of the screen ---- */

  if ((isProjectLoading && !project) || filesData === undefined || state === 'loading') {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Files')}</h1>
          <p className="page__lede">{t('Uploads and images your app uses.')}</p>
        </header>
        <Skeleton shape="row" count={6} label={t('Loading your files')} />
      </div>
    );
  }

  if (projectError || filesError) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Files')}</h1>
        </header>
        <EmptyState mark="file" sentence={t("We couldn't load your files. Try refreshing the page.")} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Files')}</h1>
        </header>
        <EmptyState
          mark="file"
          sentence={t('We cannot find that project.')}
          action={
            <Button variant="outline" onClick={() => navigate('/projects')}>
              {t('Projects')}
            </Button>
          }
        />
      </div>
    );
  }

  const environment = environmentOf(project);
  const base = `/p/${encodeURIComponent(project.slug)}`;

  // Emptiness is counted after this session's deletions, so deleting the last
  // file lands on "your app hasn't stored any files yet" rather than on an
  // empty folder inside a store that no longer has one.
  const storeEmpty = state === 'empty-store' || (tree.count === 0 && uploads.length === 0);
  const folderEmpty = !storeEmpty && (state === 'empty-folder' || visible.length === 0);

  // Exactly one ink block, chosen once. Over the limit it belongs to the way
  // out, and on an empty screen it belongs to the empty state's own action,
  // because two solid controls on one screen is two next actions.
  const ink: 'upload' | 'empty' | 'usage' = overQuota
    ? 'usage'
    : storeEmpty || folderEmpty
      ? 'empty'
      : 'upload';

  const crumbs = folder === '' ? [] : folder.split('/');
  // No target at the top of the store: "Drop to upload to /" is a sentence
  // that ends in a piece of punctuation nobody reads as a place.
  const target = folder === '' ? undefined : `/${folder}`;

  const rows = state === 'empty-folder' ? [] : visible.slice(0, shown);
  const capped = visible.length > shown;

  const codeHint = t('This file comes from your repository. It comes back the next time you deploy.');

  const uploadButton = USE_MOCK ? (
    <FilePickerButton
      label={t('Upload')}
      variant={ink === 'upload' ? 'solid' : 'outline'}
      disabled={overQuota}
      onFiles={addFiles}
    />
  ) : null;

  function deleteFile(file: FileObj) {
    setRemoved((was) => [...was, file.id]);
    setDeleting(null);
    setOpened(null);
    if (file.source === 'from-your-code') {
      showToast(t('Deleted until your next deploy.'), file.name);
      return;
    }
    showToast(t('File deleted.'), file.name);
  }

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Files')}</h1>
        <p className="page__lede">{t('Uploads and images your app uses.')}</p>
      </header>

      <div className="fbrowse__bar">
        <nav className="fcrumb" aria-label={t('Folder')}>
          <button type="button" className="fcrumb__seg" onClick={() => setFolder('')}>
            {t('Files')}
          </button>
          {crumbs.map((segment, index) => {
            const path = crumbs.slice(0, index + 1).join('/');
            const last = index === crumbs.length - 1;
            return (
              <span className="fcrumb__part" key={path}>
                <span className="fcrumb__sep" aria-hidden="true">
                  /
                </span>
                <button
                  type="button"
                  className="fcrumb__seg mono"
                  data-current={last ? 'yes' : 'no'}
                  aria-current={last ? 'page' : undefined}
                  onClick={() => setFolder(path)}
                >
                  {segment}
                </button>
              </span>
            );
          })}
        </nav>

        <div className="fbrowse__tools">
          {/* The cap, stated before anyone drags anything. It is a normal
              condition of the plan, not a warning, so it is flat and quiet. */}
          <span className="fbrowse__cap">
            {t('Files can be up to {cap}.', { cap: formatBytes(MAX_UPLOAD_BYTES) })}
          </span>
          {uploadButton}
        </div>
      </div>

      <DropZone
        target={target}
        blocked={overQuota || !USE_MOCK}
        blockedSentence={
          USE_MOCK
            ? t('Uploads are paused while you are over your file storage limit.')
            : t("Uploading here isn't available yet. Ask your coding agent to add files instead.")
        }
        onFiles={addFiles}
      >
        {overQuota && storage ? (
          <Notice
            tone="attention"
            title={t("You're over your file storage limit.")}
            actions={
              <Button variant="solid" onClick={() => navigate(`${base}/usage`)}>
                {t('See usage')}
              </Button>
            }
          >
            <p>
              {t(
                'Your app keeps running and your files stay where they are. New uploads are paused until you free some space.',
              )}
            </p>
          </Notice>
        ) : null}

        <div className="fbrowse">
          <nav className="ftree" aria-label={t('Folders')}>
            <ul className="ftree__list">
              <FolderBranch node={tree} depth={0} selected={folder} onPick={setFolder} />
            </ul>
          </nav>

          <div className="fbrowse__main">
            {/* The tray. The one thing on this screen that moves. */}
            {inFlight.length + notLanded.length > 0 ? (
              <section className="ftray" aria-label={t('Uploads')}>
                <p className="ftray__head">
                  {inFlight.length === 1
                    ? t('Uploading 1 file')
                    : inFlight.length > 1
                      ? t('Uploading {n} files', { n: count(inFlight.length) })
                      : notLanded.length === 1
                        ? t("1 file didn't upload")
                        : t("{n} files didn't upload", { n: count(notLanded.length) })}
                </p>

                {/* One polite announcement for the whole tray, naming the file
                    being transferred. A live region per row would read the
                    entire tray on every tick. */}
                <span className="ftray__say" role="status" aria-live="polite">
                  {inFlight[0]
                    ? t('Uploading {name}', { name: inFlight[0].upload.name })
                    : ''}
                </span>

                <FileList label={t('Uploads')}>
                  {[...inFlight, ...notLanded].map(({ upload, at }) => (
                    <FileRow
                      key={upload.id}
                      name={upload.name}
                      bytes={upload.bytes}
                      kind={fileKind(upload.contentType, upload.name)}
                      upload={{
                        status: at.status,
                        fraction: at.fraction,
                        reason:
                          at.status === 'didnt-upload' && upload.refusal
                            ? refusalSentence(upload.refusal, upload.bytes)
                            : undefined,
                      }}
                      action={
                        at.status === 'didnt-upload' ? (
                          upload.refusal === 'stopped' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setUploads((was) =>
                                  was.map((one) =>
                                    one.id === upload.id
                                      ? { ...one, startedAt: Date.now(), refusal: null }
                                      : one,
                                  ),
                                )
                              }
                            >
                              {t('Try again')}
                            </Button>
                          ) : (
                            // Trying again on a file that is refused by rule
                            // would refuse it again. Offering it would be a
                            // button that exists to fail.
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setUploads((was) => was.filter((one) => one.id !== upload.id))
                              }
                            >
                              {t('Remove')}
                            </Button>
                          )
                        ) : undefined
                      }
                    />
                  ))}
                </FileList>
              </section>
            ) : null}

            {storeEmpty ? (
              <EmptyState
                mark="file"
                sentence={t("Your app hasn't stored any files yet.")}
                context={t('Your app can also save files here on its own.')}
                action={
                  USE_MOCK ? (
                    <FilePickerButton
                      label={t('Upload')}
                      variant={ink === 'empty' ? 'solid' : 'outline'}
                      disabled={overQuota}
                      onFiles={addFiles}
                    />
                  ) : undefined
                }
              />
            ) : folderEmpty ? (
              <EmptyState
                mark="file"
                sentence={t('Nothing in this folder yet.')}
                action={
                  USE_MOCK ? (
                    <FilePickerButton
                      label={t('Upload')}
                      variant={ink === 'empty' ? 'solid' : 'outline'}
                      disabled={overQuota}
                      onFiles={addFiles}
                    />
                  ) : undefined
                }
              />
            ) : (
              <section className="fbrowse__list" aria-label={t('Files in this folder')}>
                <FileList label={t('Files in this folder')}>
                  {rows.map((file) => (
                    <FileRow
                      key={file.id}
                      name={relativeName(file.path, folder)}
                      bytes={file.bytes}
                      kind={fileKind(file.contentType, file.name)}
                      when={whenLabelOrNull(file.updatedAt) ?? undefined}
                      fromYourCode={file.source === 'from-your-code'}
                      fromYourCodeHint={codeHint}
                      onOpen={() => setOpened(file.id)}
                    />
                  ))}
                </FileList>

                {/* The cap is a normal condition, not a failure. It reads the
                    same way the row cap reads above a table. */}
                {capped ? (
                  <div className="fbrowse__more">
                    <p className="fbrowse__cap">
                      {t('Showing the first {n} files.', { n: count(shown) })}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setShown((was) => was + FILES_SHOWN)}
                    >
                      {t('Show more')}
                    </Button>
                  </div>
                ) : null}
              </section>
            )}

            {/* The footer strip is a measurement, and a bar is drawn only
                when there is something to measure. The usage bar's zero label
                is written for requests ("No traffic yet"), which is the wrong
                sentence about storage, and a project whose storage has not
                been measured yet would sit under a bar contradicting the file
                count immediately to its left. The count is the content here,
                which is the same reason the bar is decoration everywhere else. */}
            {!storeEmpty ? (
              <div className="ffoot" data-measured={storage && storage.used > 0 ? 'yes' : 'no'}>
                <span className="ffoot__count mono">{countLabel(visible.length)}</span>
                {storage && storage.used > 0 ? (
                  <UsageBar
                    label={t('File storage')}
                    used={storage.used}
                    limit={storage.limit}
                    unit="bytes"
                    compact
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </DropZone>

      {/* Upload links sit below the browser and above Advanced, because the
          question they answer ("how does somebody else get files to me?")
          only occurs to a person who is already looking at their files. Real
          builds only: there is no live route behind it in the mock, and a
          demo that minted a dead URL would be worse than its absence. */}
      {!USE_MOCK && project ? (
        <div className="fbrowse__links">
          <UploadLinkPanel projectId={project.id} projectSlug={project.slug} />
        </div>
      ) : null}

      {/* Advanced appends, below everything, and changes nothing above it. */}
      <div className="fbrowse__advanced">
        <AdvancedDisclosure>
          <dl className="idents">
            <div className="idents__row">
              <dt className="idents__key">{t('Files')}</dt>
              <dd className="idents__value mono">{count(tree.count)}</dd>
            </div>
            <div className="idents__row">
              <dt className="idents__key">{t('Exact size')}</dt>
              <dd className="idents__value mono">
                {tree.bytes === 1 ? t('1 byte') : t('{n} bytes', { n: count(tree.bytes) })}
              </dd>
            </div>
          </dl>
        </AdvancedDisclosure>
      </div>

      {openFile ? (
        <FileSheet
          file={openFile}
          webAddress={environment.webAddress}
          onClose={() => setOpened(null)}
          onDelete={
            USE_MOCK
              ? () => {
                  setOpened(null);
                  setDeleting(openFile.id);
                }
              : undefined
          }
        />
      ) : null}

      {deletingFile && deletingFile.source === 'from-your-code' ? (
        <RepoFileConfirm
          file={deletingFile}
          onClose={() => setDeleting(null)}
          onConfirm={() => deleteFile(deletingFile)}
        />
      ) : null}

      {deletingFile && deletingFile.source !== 'from-your-code' ? (
        <DestructiveConfirm
          open
          onClose={() => setDeleting(null)}
          onConfirm={() => deleteFile(deletingFile)}
          title={t('Delete {name}?', { name: deletingFile.name })}
          objectName={deletingFile.name}
          confirmLabel={t('Delete {name}', { name: deletingFile.name })}
          consequences={[
            {
              kind: 'loss',
              text: t("This file is deleted from your file storage. This can't be undone."),
            },
            {
              kind: 'loss',
              // One sentence for every kind of file: a spreadsheet that goes
              // missing does not leave a broken image behind it.
              text: t('If your app still asks for this file, visitors get an error where it used to be.'),
            },
            { kind: 'survives', text: t('Your other files stay exactly where they are.') },
          ]}
        />
      ) : null}
    </div>
  );
}
